import * as dateFnsCompat from 'utils/date-fns-compat';
import fp from 'lodash/fp';
import React from 'react';
import { Breadcrumb, BreadcrumbItem, Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { compose, lifecycle, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { cpdActivityMap, dateFormats, routes } from '../../../../constants';
import { getUserIndustryGoals, isBusinessUser, mapUserCpdCategorys, startOfNextCpdPeriod, startOfThisCpdPeriod } from '../../../../services';
import { getCpdActivityColumns, mapCpdActivity } from '../../../../services/cpd-activity';
import { cpdCategorysActions } from '../../../../state/modules/areas-of-advice';
import { cpdActivityHitsSelector, cpdPointActions, cpdPointsSelector, exportCpdSelector } from '../../../../state/modules/cpd-points';
import { publisherActions } from '../../../../state/modules/publisher';
import { currentUserCpdDaysRemaining, currentUserSelector } from '../../../../state/modules/user';
import { downloadBlobStreamFile, getBlobStream, getCsvStream, writeDataToCsvStream } from '../../../../utils';
import { DateSelect, Icon } from '../../../components';
import { CpdActivityTable } from './components';
import { format } from 'date-fns';

const exportTypes = {
  pdf: 'PDF',
  csv: 'CSV'
};

const CpdActivity = ({
  cpdActivity,
  onDateRangeChange,
  loadMore,
  sortBy,
  filter,
  cpdActivityHits,
  exportCsv,
  exportPdf,
  rejectText
}) => (
    <div className='base-layout'>
      <section id='header--profile' key='cpd-activity-header' className='layout--header'>
        <Grid>
          <Row className='sm-gutter'>
            <Col xs={12} md={5}>
              <Breadcrumb>
                <LinkContainer to={routes.app.professionalProfile.root}>
                  <BreadcrumbItem>
                    Professional profile
                  </BreadcrumbItem>
                </LinkContainer>
                <BreadcrumbItem active>
                  CPD Activity
                </BreadcrumbItem>
              </Breadcrumb>
            </Col>
          </Row>
          <Row className='button-gutter'>
            <Col xs={12} md={5}>
              <h1>
                <Icon icon='cpd-activity-large' /> CPD Activity
              </h1>
              <hr />
            </Col>
            <Col xs={12} md={2} mdOffset={1}>
              <LinkContainer
                to={routes.app.externalCpd.details}
                className='large full pull-down'>
                <Button>
                  Add External CPD <Icon icon='add-large' />
                </Button>
              </LinkContainer>
            </Col>
            <Col xs={12} md={2}>
              <Button
                className='large full pull-down'
                onClick={exportCsv}>
                <Icon iconSet='glyphicon' icon='download-alt' className="space-right" /> export to csv
              </Button>
            </Col>
            <Col xs={12} md={2}>
              <LinkContainer
                to={routes.app.professionalProfile.progressReport}
                className='large full pull-down'>
                <Button>
                  progress report
                </Button>
              </LinkContainer>
            </Col>
          </Row>
        </Grid>
      </section>
      <div id='section--cpd-activity' key='cpd-activity-section' className='layout--section'>
        <Grid>
          <Row className='no-gutter sectionHeader'>
            <form>
              <table className='table--date-search'>
                <tbody>
                  <tr>
                    <td>
                      Show activity between
                    </td>
                    <td>
                      <DateSelect date={filter.fromDate} onChange={(date) => onDateRangeChange('fromDate', date)} />
                    </td>
                    <td>
                      and
                    </td>
                    <td>
                      <DateSelect date={filter.toDate} onChange={(date) => onDateRangeChange('toDate', date)} futureYears={1} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </form>
          </Row>
          <CpdActivityTable
            className='table--cpd-activity'
            cpdActivity={cpdActivity}
            cpdActivityMap={cpdActivityMap(rejectText)}
            sortBy={sortBy}
          />
          {
            cpdActivity.length < cpdActivityHits && cpdActivity.length > 0 ?
              <Row className='no-gutter row-slider'>
                <Col xs={12} md={4} mdOffset={4}>
                  <Button
                    className='large full'
                    onClick={loadMore}>
                    Load more CPD activity
                  </Button>
                </Col>
              </Row> :
              null
          }
        </Grid>
      </div>
    </div>
  );

const mapStateToProps = ({ cpdCategorys, industry, publisher, ...state }) => ({
  cpdActivity: cpdPointsSelector(state),
  exportActivity: exportCpdSelector(state),
  cpdCategorys: cpdCategorys.cpdCategorys,
  publishers: publisher.publishers,
  isFetchingCpdCategorys: cpdCategorys.isFetching,
  isFetchingPublishers: publisher.isFetching,
  cpdActivityHits: cpdActivityHitsSelector(state),
  user: currentUserSelector(state),
  userCpdCategorys: mapUserCpdCategorys(cpdCategorys.cpdCategorys)(fp.get('currentUserCpdCategorys')(state.user)),
  totalCpdProgress: fp.get('currentUserCpdCategorys')(state.user),
  cpdDaysRemaining: currentUserCpdDaysRemaining(state),
  industryCpdCategorys: fp.get('cpdCategorys')(industry),
  userIndustryGoals: getUserIndustryGoals(state.user.profile, fp.get('industriesWithCpdCategorys')(industry))
});

const mapDispatchToProps = (dispatch) => ({
  getCpdCategorys: bindActionCreators(cpdCategorysActions.cpdCategorys.get, dispatch),
  getPublishers: bindActionCreators(publisherActions.publishers.get, dispatch),
  getCpdPoints: bindActionCreators(cpdPointActions.cpdPoints.get, dispatch),
  getExportCpdPoints: bindActionCreators(cpdPointActions.cpdPoints.getExport, dispatch),
  loadMoreCpdPoints: bindActionCreators(cpdPointActions.cpdPoints.loadMore, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(
    ({ cpdDaysRemaining, industryCpdCategorys }) => ({
      filter: {
        skip: 0,
        fromDate: format(startOfThisCpdPeriod(cpdDaysRemaining), dateFormats.FilteringDate),
        toDate: format(startOfNextCpdPeriod(cpdDaysRemaining), dateFormats.FilteringDate),
        sort: '',
        take: 20,
        cpdCategorys: fp.map('id')(industryCpdCategorys).join(',')
      },
      exportType: undefined
    }),
    {
      onQuerying: () => (filter) => ({ filter }),
      setExportType: () => (exportType) => ({ exportType })
    }
  ),
  withProps(({ user, cpdDaysRemaining }) => ({
    rejectText: isBusinessUser(user) ? "(rejected)" : "(draft)",
    exportFilter: {
      fromDate: format(startOfThisCpdPeriod(cpdDaysRemaining), dateFormats.FilteringDate),
      toDate: format(startOfNextCpdPeriod(cpdDaysRemaining), dateFormats.FilteringDate)
    }
  })),
  lifecycle({
    componentDidMount() {
      const {
        cpdCategorys,
        isFetchingCpdCategorys,
        getCpdCategorys,
        publishers,
        isFetchingPublishers,
        getPublishers,
        getCpdPoints,
        filter
      } = this.props;

      if (!cpdCategorys.length && !isFetchingCpdCategorys) {
        getCpdCategorys();
      }

      if (!publishers.length && !isFetchingPublishers) {
        getPublishers();
      }
      getCpdPoints(filter);
    },
    componentWillReceiveProps(nextProps) {
      const {
        exportActivity,
        publishers,
        industryCpdCategorys,
        user,
        filter,
        userIndustryGoals
      } = this.props;
      const {
        exportActivity: cpdActivity,
        exportType
      } = nextProps;


      if (!exportActivity && cpdActivity) {
        if (exportType === exportTypes.csv) {
          const columns = getCpdActivityColumns(industryCpdCategorys, userIndustryGoals)
          const rows = mapCpdActivity(industryCpdCategorys, publishers, userIndustryGoals)(
            fp.flow(
              fp.uniqBy(
                ({ userCpdPointId, cpdCategoryId }) => `${userCpdPointId}_${cpdCategoryId}`
              ),
              fp.sortBy(
                ({ timeEarned }) => {
                  return -1 * dateFnsCompat.parse(timeEarned).getTime()
                }
              )
            )(cpdActivity)
          )
          const {
            firstName,
            lastName
          } = user
          
          const endDate = dateFnsCompat.format(dateFnsCompat.parse(filter.toDate), dateFormats.ShortDate).replace(/\//g, '-')
          
          downloadBlobStreamFile(
            writeDataToCsvStream(
              getCsvStream({
                header: true,
                columns
              }),
              rows
            ).pipe(
              getBlobStream()
            ),
            `${firstName}-${lastName}-CPD-activity-${endDate}.csv`
          );
        }
      }
    }
  }),
  withHandlers({
    sortBy: ({ getCpdPoints, filter, onSort, onQuerying }) => (field, sortOrder) => {
      const sortParam = sortOrder ? `${field},${sortOrder}` : undefined;
      const nextFilter = { ...filter, sort: sortParam, skip: 0 };
      onQuerying(nextFilter);
      getCpdPoints(nextFilter);
    },
    loadMore: ({ loadMoreCpdPoints, filter, cpdActivity, onQuerying }) => () => {
      const nextFilter = { ...filter, skip: cpdActivity.length };
      onQuerying(nextFilter);
      loadMoreCpdPoints(nextFilter);
    },
    onDateRangeChange: ({ getCpdPoints, filter, onQuerying, setDateRange }) => (key, date) => {
      const nextFilter = { ...filter, [key]: format(date, dateFormats.FilteringDate), skip: 0 };

      onQuerying(nextFilter);
      
      if (nextFilter.fromDate && nextFilter.toDate) {
        getCpdPoints(nextFilter);
      }
    },
    exportCsv: ({
      filter,
      getExportCpdPoints,
      setExportType
    }) => (e) => {
      setExportType(exportTypes.csv)
      getExportCpdPoints({
        ...filter,
        skip: 0,
        take: undefined
      });
    }
  })
)(CpdActivity);



// WEBPACK FOOTER //
// ./src/containers/pages/app/professional-profile/cpd-activity.js